import { createTheme } from '@mui/material/styles';

const primaryMain = '#2f4ae1';
const secondaryMain = '#12b59f';
const backgroundDefault = '#f5f7fb';
const backgroundPaper = '#ffffff';
const borderColor = '#d8deeb';
const textPrimary = '#1f2937';
const textSecondary = '#4b5563';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: primaryMain,
      contrastText: '#ffffff'
    },
    secondary: {
      main: secondaryMain,
      contrastText: '#ffffff'
    },
    background: {
      default: backgroundDefault,
      paper: backgroundPaper
    },
    text: {
      primary: textPrimary,
      secondary: textSecondary
    },
    divider: borderColor,
    info: {
      main: '#3b82f6'
    },
    success: {
      main: '#16a34a'
    },
    warning: {
      main: '#f59e0b'
    },
    error: {
      main: '#dc2626'
    }
  },
  typography: {
    fontFamily: "Inter, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    h1: { fontSize: '2.75rem', fontWeight: 600, letterSpacing: '-0.01em' },
    h2: { fontSize: '2.25rem', fontWeight: 600, letterSpacing: '-0.01em' },
    h3: { fontSize: '1.75rem', fontWeight: 600 },
    h4: { fontSize: '1.5rem', fontWeight: 600 },
    h5: { fontSize: '1.25rem', fontWeight: 600 },
    h6: { fontSize: '1.125rem', fontWeight: 600 },
    subtitle1: { fontSize: '1rem', fontWeight: 500 },
    subtitle2: { fontSize: '0.925rem', fontWeight: 500 },
    body1: { fontSize: '0.95rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 }
  },
  shape: {
    borderRadius: 12
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: backgroundDefault,
          color: textPrimary
        },
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: `${borderColor} transparent`
        },
        '*::-webkit-scrollbar': {
          width: '8px',
          height: '8px'
        },
        '*::-webkit-scrollbar-track': {
          background: 'transparent'
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: borderColor,
          borderRadius: '4px'
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: textSecondary
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(12px)',
          backgroundColor: 'rgba(255,255,255,0.9)',
          color: textPrimary,
          borderBottom: `1px solid ${borderColor}`,
          boxShadow: 'none'
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 72,
          '@media (min-width:600px)': {
            paddingLeft: 24,
            paddingRight: 24
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)',
          transition: 'box-shadow 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 20px 48px rgba(15, 23, 42, 0.12)'
          }
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          borderRadius: 999,
          textTransform: 'none',
          fontWeight: 600,
          paddingInline: 20,
          paddingBlock: 10,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-1px)'
          }
        },
        outlined: {
          borderColor,
          backgroundColor: '#ffffff'
        },
        containedPrimary: {
          boxShadow: '0 10px 20px rgba(47, 74, 225, 0.24)',
          '&:hover': {
            boxShadow: '0 12px 24px rgba(47, 74, 225, 0.32)'
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginInline: 4,
          marginBlock: 2
        }
      }
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.75rem',
          color: textSecondary,
          lineHeight: 1.4
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          backgroundColor: '#ffffff'
        },
        input: {
          paddingBlock: 12
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 500
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          paddingBlock: 12
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${borderColor}`,
          boxShadow: 'none',
          '&:before': {
            display: 'none'
          },
          '&.Mui-expanded': {
            margin: '12px 0'
          }
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            fontWeight: 600,
            color: textSecondary,
            borderBottom: `1px solid ${borderColor}`
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor,
          fontSize: '0.9rem'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 500
        }
      }
    }
  }
});

export default theme;
